import { CrossIcon } from 'assets/icons';
import Button from 'components/button';
import IconTooltip from 'components/iconTooltip/IconTooltip';
import StyledInput from 'components/styledInput/StyledInput';
import { useState } from 'react';
import ApiDataBase from 'server';
import {
  TASK_CHECK_TIMEOUT_MS,
  TASK_INPUT_MAX_LENGTH,
  addBannerTaskAsNew,
  addPendingTask,
  applyListedTaskLabel,
  applyTaskCheckResult,
  canAddBannerTaskAsNew,
  cleanTaskLabel,
  countTemplateTasks,
  createPendingTaskKey,
  getAddedTaskItems,
  getExistingTaskLabels,
  getQualificationTaskRows,
  getTaskBannerLines,
  isTaskBanner,
  readTaskCheckAnswer,
  removeCompanyTask,
  removePendingTask,
} from './templateFormData';
import { templateText } from './templateTexts';

const JOB_TITLE_MAX_LENGTH = 120;

// The small badge of the company app (badge badge-sm): 12px medium text, 6px corners.
const TASK_BADGE_CLASS =
  'inline-flex items-center whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium';

/**
 * Asks the AI to check an added task and settles it in the form once, on the
 * first of the answer and the timeout. setForm is the wizard's setter: the
 * answer still lands when the admin has moved to another step meanwhile.
 */
const checkAddedTask = ({ key, label, jobTitle, existingTasks, setForm }) => {
  let settled = false;
  let timer = null;
  const settle = (answer) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    const result = readTaskCheckAnswer(answer);
    setForm((prev) => applyTaskCheckResult(prev, key, result));
  };
  timer = setTimeout(() => settle(null), TASK_CHECK_TIMEOUT_MS);
  const title = cleanTaskLabel(jobTitle).slice(0, JOB_TITLE_MAX_LENGTH);
  ApiDataBase.postAdminCheckMissionTemplateTask(
    {
      label,
      ...(title ? { jobTitle: title } : {}),
      ...(existingTasks.length ? { existingTasks } : {}),
    },
    settle,
    () => settle(null)
  );
};

/** The tasks of a job description: the qualification's, ticked or not, and
 *  the ones the admin adds on top. A mission lists there the tasks it holds
 *  beyond its qualification, which the admin did not necessarily add: hence
 *  `addedLabel`. */
const TemplateTasksSection = ({ form, setForm, addedLabel }) => {
  const [newLabel, setNewLabel] = useState('');
  const rows = getQualificationTaskRows(form);
  const addedItems = getAddedTaskItems(form);
  const { selected, total } = countTemplateTasks(form);
  const cleanLabel = cleanTaskLabel(newLabel);
  const notice = form.taskNotice;
  const bannerLines = getTaskBannerLines(notice);

  const clearNotice = () =>
    setForm((prev) => (prev.taskNotice ? { ...prev, taskNotice: null } : prev));

  // Typing only clears an error: a banner stays until replaced or closed.
  const clearErrorNotice = () =>
    setForm((prev) =>
      prev.taskNotice && !isTaskBanner(prev.taskNotice)
        ? { ...prev, taskNotice: null }
        : prev
    );

  const addTask = () => {
    if (!cleanLabel) return;
    // A task already listed needs no AI call: it is ticked or pointed at.
    if (applyListedTaskLabel(form, cleanLabel)) {
      setForm((prev) => applyListedTaskLabel(prev, cleanLabel) || prev);
      setNewLabel('');
      return;
    }
    const key = createPendingTaskKey();
    setForm((prev) => addPendingTask(prev, cleanLabel, key));
    setNewLabel('');
    checkAddedTask({
      key,
      label: cleanLabel,
      jobTitle: form.qualificationLabel,
      existingTasks: getExistingTaskLabels(form),
      setForm,
    });
  };

  const addBannerTask = (lineId) =>
    setForm((prev) => addBannerTaskAsNew(prev, lineId));

  const removeTask = (item) =>
    setForm((prev) =>
      item.pending
        ? removePendingTask(prev, item.key)
        : removeCompanyTask(prev, item.label)
    );

  const toggleTask = (row) =>
    setForm((prev) => ({
      ...prev,
      taskChecks: { ...(prev.taskChecks || {}), [row.key]: !row.checked },
    }));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col">
        <span className="ft-xl font-medium text-gray-900">
          {templateText('tasks.title')}
        </span>
        <span className="ft-sm text-gray-500">
          {templateText('tasks.description')}
        </span>
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-end gap-2">
          <div className="flex-1 min-w-0">
            <StyledInput
              type="text"
              label={
                <span className="sr-only">
                  {templateText('tasks.addPlaceholder')}
                </span>
              }
              value={newLabel}
              maxLength={TASK_INPUT_MAX_LENGTH}
              onChange={(event) => {
                setNewLabel(event.target.value);
                clearErrorNotice();
              }}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                addTask();
              }}
              placeholder={templateText('tasks.addPlaceholder')}
            />
          </div>
          {/* Centred on the 40 px input, below its screen-reader label row. */}
          <div className="h-10 flex items-center">
            <Button
              size="SM"
              color={cleanLabel ? 'SECONDARY' : 'DISABLED'}
              onClick={addTask}
              disabled={!cleanLabel}
            >
              {templateText('tasks.addButton')}
            </Button>
          </div>
        </div>
        {notice?.type === 'duplicate' ? (
          <span className="ft-sm text-red-500">
            {templateText('tasks.duplicate')}
          </span>
        ) : null}
        {notice?.type === 'rejected' ? (
          <span className="ft-sm text-red-500">
            {templateText('tasks.rejected', { label: notice.label })}
          </span>
        ) : null}
        {notice?.type === 'notChecked' ? (
          <span className="ft-sm text-red-500 break-words">
            {templateText('tasks.notCheckedTooLong', { label: notice.label })}
          </span>
        ) : null}
        {bannerLines.length ? (
          <div
            role="status"
            className="flex items-start justify-between gap-2 px-3 py-2 bg-gray-50 border border-gray-100 rounded-[0.4rem]"
          >
            <div className="flex flex-col items-start gap-2 min-w-0">
              {bannerLines.map((line) => (
                <div
                  key={line.id}
                  className="flex flex-col items-start gap-1 min-w-0"
                >
                  {line.type === 'rejected' ? (
                    <span className="ft-sm text-red-500 break-words">
                      {templateText('tasks.rejected', { label: line.text })}
                    </span>
                  ) : (
                    <span className="ft-sm text-gray-500 break-words">
                      {templateText(`tasks.${line.type}`, {
                        match: line.match,
                      })}
                    </span>
                  )}
                  {canAddBannerTaskAsNew(form, line.id) ? (
                    <button
                      type="button"
                      onClick={() => addBannerTask(line.id)}
                      className="ft-sm text-gray-500 underline hover:text-gray-700"
                    >
                      {templateText('tasks.addAsNewTask')}
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
            <button
              type="button"
              aria-label={templateText('tasks.closeNotice')}
              onClick={clearNotice}
              className="flex items-center justify-center w-6 h-6 shrink-0 rounded hover:bg-gray-100"
            >
              <span aria-hidden="true">
                <CrossIcon wh={16} color="#6B7280" />
              </span>
            </button>
          </div>
        ) : null}
      </div>

      <span className="text-sm text-gray-700">
        <strong>
          {templateText(
            selected > 1
              ? 'tasks.selectedCountOther'
              : 'tasks.selectedCountOne',
            { count: selected }
          )}
        </strong>{' '}
        {templateText(
          selected > 1 ? 'tasks.selectedOfOther' : 'tasks.selectedOfOne',
          { total }
        )}
      </span>

      {rows.length === 0 && addedItems.length === 0 ? (
        <span className="text-sm text-gray-500">
          {templateText('tasks.noProposal')}
        </span>
      ) : (
        // Same list as the company app: one bordered card, the checkbox says
        // what is selected, no coloured border. No overflow-hidden: it would clip
        // the remove tooltip of the first row; the end rows round their own hover.
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-100 bg-white">
          {addedItems.map((item) => (
            <li
              key={item.key}
              aria-busy={item.pending ? 'true' : undefined}
              className="flex items-center justify-between gap-4 px-5 py-3 hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
            >
              <div className="flex min-w-0 items-center gap-2">
                {/* Always selected: checked, as the proposed tasks; the cross removes it. */}
                <input
                  type="checkbox"
                  checked
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                  className={`checkbox-primary pointer-events-none mr-1 ${
                    item.pending ? 'opacity-40' : ''
                  }`}
                />
                <span
                  className={
                    item.pending
                      ? 'rounded-md border border-dashed border-gray-300 px-2 py-0.5 text-sm text-gray-400 break-words'
                      : 'text-sm text-gray-900 break-words'
                  }
                >
                  {item.label}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span
                  className={`${TASK_BADGE_CLASS} ${
                    item.pending
                      ? 'border border-dashed border-gray-300 bg-white text-gray-400'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {item.pending
                    ? templateText('tasks.checking')
                    : addedLabel || templateText('tasks.addedByCompany')}
                </span>
                <IconTooltip label={templateText('tasks.removeTooltip')}>
                  <button
                    type="button"
                    onClick={() => removeTask(item)}
                    className="shrink-0 p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  >
                    <span className="sr-only">
                      {templateText('tasks.remove', { label: item.label })}
                    </span>
                    <span aria-hidden="true">
                      <CrossIcon wh={20} color="#111827" />
                    </span>
                  </button>
                </IconTooltip>
              </div>
            </li>
          ))}
          {rows.map((row) => (
            <li
              key={row.key}
              className="flex px-5 py-4 hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
            >
              <StyledInput
                type="checkbox"
                checked={!!row.checked}
                onChange={() => toggleTask(row)}
                label={<span className="min-w-0 break-words">{row.label}</span>}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TemplateTasksSection;
